import api from './api';
import { redactLinkCredentialsDeep } from '../utils/redact-link-credentials';

interface CheckoutState {
  currentStep: number;
  formData: {
    name?: string;
    email?: string;
    whatsapp?: string;
    hasAcceptedTerms?: boolean;
    hasWhatsappOptin?: boolean;
  };
  children: Array<{
    name: string;
    birthDate: string;
    gender: string;
  }>;
  address: {
    cep?: string;
    rua?: string;
    numero?: string;
    bairro?: string;
    cidade?: string;
    estado?: string;
    complemento?: string;
  };
  plan: {
    id?: string;
    name?: string;
    price?: number;
    billingCycle?: string;
  };
  coupon?: {
    code?: string;
    discount?: number;
    type?: string;
  };
  payment: {
    method?: 'pix' | 'card';
    lastFourDigits?: string;
  };
}

interface SessionInfo {
  sessionId: string;
  sessionStart: string;
  pageViews: number;
  referrer: string;
  utmParams: {
    source?: string;
    medium?: string;
    campaign?: string;
    term?: string;
    content?: string;
  };
  timeOnPage: number;
}

interface ErrorLog {
  message: string;
  stack?: string;
  type: 'payment_error' | 'network_error' | 'validation_error' | 'system_error';
  severity: 'low' | 'medium' | 'high' | 'critical';
  context: {
    url: string;
    userAgent: string;
    timestamp: string;
    component?: string;
    action?: string;
    paymentMethod?: 'pix' | 'card';
    errorCode?: string;
    clientId?: string;
    planId?: string;
    orderId?: string;
    checkoutState?: Partial<CheckoutState>;
    sessionInfo?: SessionInfo;
    localStorage?: Record<string, unknown>;
    additionalInfo?: Record<string, unknown>;
  };
  browserInfo?: {
    language: string;
    platform: string;
    cookieEnabled: boolean;
    onLine: boolean;
    screenResolution: string;
    viewport: string;
    memory?: number;
    cores?: number;
  };
}

/**
 * #665: o envio que falha deixava a fila crescer a cada 30 s. Acima de 256 KB
 * o backend responde 413 antes do CORS (o navegador só vê `ERR_NETWORK`) e a
 * aba não manda mais relato nenhum até recarregar. A fila guarda os últimos
 * `MAX_FILA`, o lote leva no máximo `MAX_LOTE` e, depois de `MAX_FALHAS_LOTE`
 * falhas seguidas, o lote é descartado para o resto voltar a sair.
 */
export const MAX_FILA = 20;
export const MAX_LOTE = 10;
export const MAX_FALHAS_LOTE = 3;

class ErrorTrackingService {
  private errorQueue: ErrorLog[] = [];
  private enviandoLote = false;
  private falhasDoLote = 0;
  private isOnline: boolean = navigator.onLine;
  private flushInterval: NodeJS.Timeout | null = null;
  private sessionId: string;
  private sessionStart: Date;
  private pageViews: number = 0;
  private checkoutStateGetter: (() => Partial<CheckoutState>) | null = null;

  constructor() {
    this.sessionId = this.generateSessionId();
    this.sessionStart = new Date();
    this.setupEventListeners();
    this.startFlushInterval();
    this.trackPageView();
  }

  private generateSessionId(): string {
    return `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
  }

  private trackPageView() {
    this.pageViews++;
  }

  public setCheckoutStateGetter(getter: () => Partial<CheckoutState>) {
    this.checkoutStateGetter = getter;
  }

  private getUTMParams(): Record<string, string> {
    const params = new URLSearchParams(window.location.search);
    return {
      source: params.get('utm_source') || '',
      medium: params.get('utm_medium') || '',
      campaign: params.get('utm_campaign') || '',
      term: params.get('utm_term') || '',
      content: params.get('utm_content') || '',
    };
  }

  private getSessionInfo(): SessionInfo {
    const now = new Date();
    const timeOnPage = Math.floor((now.getTime() - this.sessionStart.getTime()) / 1000);

    return {
      sessionId: this.sessionId,
      sessionStart: this.sessionStart.toISOString(),
      pageViews: this.pageViews,
      referrer: document.referrer || 'direct',
      utmParams: this.getUTMParams(),
      timeOnPage,
    };
  }

  private getLocalStorageData(): Record<string, unknown> {
    const safeData: Record<string, unknown> = {};
    const keysToCapture = [
      'checkout_step',
      'checkout_plan',
      'checkout_coupon',
      'checkout_client_id',
      'checkout_children_count',
      'checkout_payment_method',
      'checkout_address_saved',
      'checkout_terms_accepted',
    ];

    try {
      // Captura apenas chaves relevantes para não expor dados sensíveis
      keysToCapture.forEach((key) => {
        const value = localStorage.getItem(key);
        if (value) {
          try {
            safeData[key] = JSON.parse(value);
          } catch {
            safeData[key] = value;
          }
        }
      });

      // Adiciona informações sobre o tamanho do localStorage
      safeData._totalKeys = localStorage.length;
      safeData._approximateSize = new Blob(Object.values(localStorage)).size;
    } catch {
      safeData._error = 'Failed to read localStorage';
    }

    return safeData;
  }

  private getCheckoutState(): Partial<CheckoutState> | undefined {
    if (!this.checkoutStateGetter) return undefined;

    try {
      return this.checkoutStateGetter();
    } catch (err) {
      console.error('Failed to get checkout state:', err);
      return undefined;
    }
  }

  private setupEventListeners() {
    window.addEventListener('online', () => {
      this.isOnline = true;
      this.flushErrors();
    });

    window.addEventListener('offline', () => {
      this.isOnline = false;
    });

    window.addEventListener('unhandledrejection', (event) => {
      this.captureError(new Error(event.reason?.message || 'Unhandled Promise Rejection'), 'system_error', 'critical', {
        component: 'global',
        action: 'unhandled_rejection',
        checkoutState: this.getCheckoutState(),
        sessionInfo: this.getSessionInfo(),
        localStorage: this.getLocalStorageData(),
        additionalInfo: {
          reason: event.reason,
          promise: event.promise,
        },
      });
    });

    window.addEventListener('error', (event) => {
      this.captureError(new Error(event.message), 'system_error', 'high', {
        component: 'global',
        action: 'window_error',
        checkoutState: this.getCheckoutState(),
        sessionInfo: this.getSessionInfo(),
        localStorage: this.getLocalStorageData(),
        additionalInfo: {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
        },
      });
    });

    // Rastreia mudanças de rota
    window.addEventListener('popstate', () => {
      this.trackPageView();
    });
  }

  private startFlushInterval() {
    this.flushInterval = setInterval(() => {
      if (this.errorQueue.length > 0 && this.isOnline) {
        this.flushErrors();
      }
    }, 30000); // Envia erros a cada 30 segundos
  }

  private getBrowserInfo() {
    const nav = navigator as Navigator & { deviceMemory?: number; hardwareConcurrency?: number };
    return {
      language: navigator.language,
      platform: navigator.platform,
      cookieEnabled: navigator.cookieEnabled,
      onLine: navigator.onLine,
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      memory: nav.deviceMemory || undefined,
      cores: nav.hardwareConcurrency || undefined,
    };
  }

  public capturePaymentError(
    error: Error | unknown,
    paymentMethod: 'pix' | 'card',
    context: Partial<ErrorLog['context']> = {},
  ) {
    const severity = this.determineSeverity(error);
    const type = 'payment_error';

    this.captureError(error, type, severity, {
      ...context,
      paymentMethod,
      action: `payment_${paymentMethod}_failed`,
      checkoutState: this.getCheckoutState(),
      sessionInfo: this.getSessionInfo(),
      localStorage: this.getLocalStorageData(),
    });
  }

  public captureNetworkError(
    error: Error | unknown,
    endpoint: string,
    method: string,
    context: Partial<ErrorLog['context']> = {},
  ) {
    this.captureError(error, 'network_error', 'high', {
      ...context,
      action: 'api_call_failed',
      checkoutState: this.getCheckoutState(),
      sessionInfo: this.getSessionInfo(),
      localStorage: this.getLocalStorageData(),
      additionalInfo: {
        endpoint,
        method,
        ...context.additionalInfo,
      },
    });
  }

  public captureValidationError(message: string, field: string, context: Partial<ErrorLog['context']> = {}) {
    this.captureError(new Error(message), 'validation_error', 'low', {
      ...context,
      action: 'validation_failed',
      checkoutState: this.getCheckoutState(),
      sessionInfo: this.getSessionInfo(),
      localStorage: this.getLocalStorageData(),
      additionalInfo: {
        field,
        ...context.additionalInfo,
      },
    });
  }

  public captureError(
    error: Error | unknown,
    type: ErrorLog['type'],
    severity: ErrorLog['severity'],
    context: Partial<ErrorLog['context']> = {},
  ) {
    const err = error as Error;
    // #626: em /cartao/<código>, /renovar/<token>, /pix/<pedido>... a URL, o
    // referrer, o endpoint e a mensagem do axios levam a credencial do link.
    const errorLog: ErrorLog = redactLinkCredentialsDeep({
      message: err?.message || String(error),
      stack: err?.stack,
      type,
      severity,
      context: {
        url: window.location.href,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
        ...context,
      },
      browserInfo: this.getBrowserInfo(),
    });

    // Adiciona à fila (só os últimos MAX_FILA)
    this.errorQueue.push(errorLog);
    if (this.errorQueue.length > MAX_FILA) {
      this.errorQueue.splice(0, this.errorQueue.length - MAX_FILA);
    }

    // Se for crítico, tenta enviar imediatamente
    if (severity === 'critical' && this.isOnline) {
      this.sendError(errorLog);
    }

    // Se a fila estiver muito grande, força o envio
    if (this.errorQueue.length >= MAX_LOTE && this.isOnline) {
      this.flushErrors();
    }

    // Log no console para debug
    console.error('[ErrorTracking]', type, severity, error, context);
  }

  private determineSeverity(error: unknown): ErrorLog['severity'] {
    // Determina severidade baseado no tipo de erro
    const axiosError = error as { response?: { status?: number }; code?: string };
    if (axiosError?.response?.status && axiosError.response.status >= 500) return 'critical';
    if (axiosError?.response?.status && axiosError.response.status >= 400) return 'high';
    if (axiosError?.code === 'NETWORK_ERROR') return 'high';
    if (axiosError?.code === 'TIMEOUT') return 'medium';
    return 'medium';
  }

  private async sendError(errorLog: ErrorLog) {
    try {
      await api.post('/logs/errors', errorLog);
      // Remove da fila se enviado com sucesso
      const index = this.errorQueue.indexOf(errorLog);
      if (index > -1) {
        this.errorQueue.splice(index, 1);
      }
    } catch (err) {
      console.error('[ErrorTracking] Falha ao enviar erro:', err);
    }
  }

  private async flushErrors() {
    if (this.errorQueue.length === 0 || this.enviandoLote) return;

    const errors = this.errorQueue.slice(0, MAX_LOTE);
    this.enviandoLote = true;

    try {
      await api.post('/logs/errors/batch', { errors });
      // Tira só o que foi enviado: o que entrou durante o envio fica
      this.errorQueue = this.errorQueue.filter((e) => !errors.includes(e));
      this.falhasDoLote = 0;
    } catch (err) {
      console.error('[ErrorTracking] Falha ao enviar batch de erros:', err);
      this.falhasDoLote++;
      if (this.falhasDoLote >= MAX_FALHAS_LOTE) {
        this.errorQueue = this.errorQueue.filter((e) => !errors.includes(e));
        this.falhasDoLote = 0;
      }
    } finally {
      this.enviandoLote = false;
    }
  }

  public destroy() {
    if (this.flushInterval) {
      clearInterval(this.flushInterval);
    }
    this.flushErrors();
  }
}

// Singleton
const errorTracker = new ErrorTrackingService();

// Exporta funções úteis
export const capturePaymentError = errorTracker.capturePaymentError.bind(errorTracker);
export const captureNetworkError = errorTracker.captureNetworkError.bind(errorTracker);
export const captureValidationError = errorTracker.captureValidationError.bind(errorTracker);
export const captureError = errorTracker.captureError.bind(errorTracker);
export const setCheckoutStateGetter = errorTracker.setCheckoutStateGetter.bind(errorTracker);

export default errorTracker;
